import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	Grid,
	Icon,
	IconButton,
	InputAdornment,
	LinearProgress,
	Pagination,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ModalContext, ToastContent } from "../../../shared/components";
import { ToolbarFinancialBordero } from "./ToolbarFinancialBordero";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { useDebounce } from "../../../shared/hooks";
import { Environment } from "../../../shared/environment";
import moment from "moment";
import "moment/locale/pt-br";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { ListSearchParams } from "../../analysis/types/list-search-params.type";
import { IBorderoPayment, IBorderoProvision, IFormCancel, IProvisionList, ProvisionService } from "../../../shared/services/api/provisoes/ProvisionService";
import { getBorderoStatusChipColors, getBorderoStatusLabel } from "../constants/borderoStatusOptions";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { IVFormErros, VForm, VTextField, useVForm } from "../../../shared/forms";
import * as yup from "yup";
import { RowMenuBordero } from "./RowMenuBordero";
import { ModalSuspendBorderoFinanceiro } from "./Modals/ModalSuspendBorderoFinanceiro";
import { ModalScheduleBordero } from "./Modals/ModalScheduleBordero";
import { useToast } from "../../../shared/hooks";
import { TFilterFields } from "../../../shared/types/filterfields.type";
import { useProfile } from "../../../context/ProfileContext";

const removeMask = (value: string) => {
	return value
		.replace(/\D/g, "")
		.replace(/[^0-9]/g, "")
		.replace(/(\d{1,2})$/, ".$1");
};

export const FinancialBorderoPresentation = () => {
	const filterAnalysis: TFilterFields[] = [
		{ name: "documento", displayName: "Documento" },
		{ name: "nome", displayName: "Nome" },
	];
	const optionfilterPeriod: TFilterFields[] = [
		{ name: "criacao", displayName: "Criação" },
		{ name: "vencimento", displayName: "Vencimento" },
		{ name: "provisionado", displayName: "Provisionamento" },
	];
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();
	const [rows, setRows] = useState<IProvisionList[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [modalPayment, setModalPayment] = useState(false);
	const [modalProvision, setModalProvision] = useState(false);
	const [modalReProvision, setModalReProvision] = useState(false);
	const [modalCancel, setModalCancel] = useState(false);
	// Suspender/Agendar (itens 6/14/16, GARV2-1463/1464) — lado do Financeiro.
	const [modalSuspend, setModalSuspend] = useState(false);
	const [modalSchedule, setModalSchedule] = useState(false);
	const { formRef } = useVForm();
	const [rowSelect, setRowSelect] = useState<IProvisionList>(); // linha selecionada
	const [idBordero, setIdBordero] = useState("");
	const [paymentBordero, setPaymentBordero] = useState("");
	const [paymentValue, setPaymentValue] = useState("0");
	const [provisionBordero, setProvisionBordero] = useState("");
	const [pesquisar, setPesquisar] = useState(true);
	const theme = useTheme();
	const toastHandler = useToast();
	const { isSuperAdmin, permissionLevel } = useProfile();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const [refreshList, setRefreshList] = useState(false);
	const hasViewPermission = useMemo(() => {
		return isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.View);
	}, [isSuperAdmin]);

	const filterInitialParams: ListSearchParams = useMemo(() => {
		return {
			tipo: "documento",
			status: "Todos",
			origem: "Todos",
			typePeriod: "criacao",
			dataIni: moment().startOf("month").subtract(24, "months").format("YYYY-MM-DD"),
			dataFim: moment().endOf("month").format("YYYY-MM-DD"),
		};
	}, []);

	const handleBusca = () => {
		setSearchParams(
			{
				busca,
				pagina: String(1),
				tipo: tipoBordero,
				status: statusBordero,
				typePeriod,
				dataini: dataIniBordero,
				datafim: dataFimBordero,
				clientFilter: clientFilter,
			},
			{ replace: true }
		);
		setPesquisar(!pesquisar);
	};

	useEffect(() => {
		setSearchParams(
			{
				...searchParams,
				dataini: filterInitialParams.dataIni,
				datafim: filterInitialParams.dataFim,
				status: filterInitialParams.status,
			},
			{ replace: true }
		);
	}, []);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const busca = useMemo(() => {
		return searchParams.get("busca") || "";
	}, [searchParams]);

	const pagina = useMemo(() => {
		return Number(searchParams.get("pagina") || "1");
	}, [searchParams]);

	const tipoBordero = useMemo(() => {
		return searchParams.get("tipo") || "";
	}, [searchParams]);

	const statusBordero = useMemo(() => {
		return searchParams.get("status") || "";
	}, [searchParams]);

	const origemBordero = useMemo(() => {
		return searchParams.get("origem") || "";
	}, [searchParams]);

	const typePeriod = useMemo(() => {
		return searchParams.get("typePeriod") || "";
	}, [searchParams]);

	const dataIniBordero = useMemo(() => {
		return searchParams.get("dataini") || "";
	}, [searchParams]);

	const dataFimBordero = useMemo(() => {
		return searchParams.get("datafim") || "";
	}, [searchParams]);

	const clientFilter = useMemo(() => {
		return searchParams.get("clientFilter") || "";
	}, [searchParams]);

	const managerId = useMemo(() => {
		return searchParams.get("managerId") || "";
	}, [searchParams]);

	useEffect(() => {
		setSearchParams(
			{
				...searchParams,
				dataini: filterInitialParams.dataIni,
				datafim: filterInitialParams.dataFim,
				tipo: filterInitialParams.tipo,
				typePeriod: filterInitialParams.typePeriod,
				status: filterInitialParams.status,
				origembordero: filterInitialParams.origem,
			},
			{ replace: true }
		);
	}, []);

	const formValidationSchemaCancel: yup.SchemaOf<IFormCancel> = yup.object().shape({
		canceledReason: yup.string().required("Campo Obrigatório"),
		// Seletor erro/real (item 13) só existe hoje no cancelamento pelo lado do Sinistro —
		// aqui no módulo Financeiro segue sem esse campo, por isso opcional.
		cancelType: yup.mixed<"erro" | "real">().optional(),
	});

	const handleBorderoPayment = (dados: IBorderoPayment) => {
		setIsLoading(true);
		debounce(() => {
			//gravar valores deve se remover a mascar
			dados.paymentValue = removeMask(dados.paymentValue);
			ProvisionService.borderoPayment(idBordero, dados).then((result) => {
				setIsLoading(false);
				if (result instanceof Error) {
					alert(result.message);
				} else {
					toastHandler.present({
						type: "success",
						position: "top-right",
						messageOrContent: <ToastContent title={"Sucesso"} content={"Pagamento registrado com sucesso"} />,
					});
					setRefreshList(true);
				}
			});
		});
	};

	const handleBorderoProvision = (dados: IBorderoProvision) => {
		setIsLoading(true);
		debounce(() => {
			ProvisionService.borderoProvision(idBordero, dados).then((result) => {
				setIsLoading(false);
				if (result instanceof Error) {
					alert(result.message);
				} else {
					toastHandler.present({
						type: "success",
						position: "top-right",
						messageOrContent: <ToastContent title={"Sucesso"} content={"Fatura Gerada com sucesso"} />,
					});
					setRefreshList(true);
				}
			});
		});
	};

	const handleCancel = (dados: IFormCancel) => {
		formValidationSchemaCancel
			.validate(dados, { abortEarly: false })
			.then((dadosvalidados) => {
				try {
					const dadosForm: IFormCancel = {
						canceledReason: dadosvalidados.canceledReason,
					};

					ProvisionService.borderoCancel(idBordero, dadosForm).then((result) => {
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={"Sucesso"} content={"Cancelado com sucesso"} />,
						});
						setRefreshList(true);
					});
				} catch (e: any) {
					toastHandler.present({
						type: "error",
						messageOrContent: e.message,
						position: "top-center",
					});
				} finally {
					setRefreshList(true);
				}
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});

		//handleOpenModal()
	};

	const goToEdit = (id: string, e: any) => {
		e.preventDefault();
		setIdBordero(id);
		//handleOpenModalAproved(id);
	};

	const goTo = (option: string, id: string, e: any, row: IProvisionList) => {
		e.preventDefault();
		setIdBordero(id);
		setRowSelect(row);

		if (option == "pagar") {
			setModalPayment(true);
			//handleOpenModalAproved(id);
		}
		if (option == "provisionar") {
			setModalReProvision(false);
			setModalProvision(true);
			// handleOpenModalRefused();
		}
		if (option == "cancelar") {
			setModalCancel(true);
			// handleOpenModalCanceled();
		}

		if (option == "reprovisionar") {
			setModalProvision(false);
			setModalReProvision(true);
			// handleOpenModalContested();
		}
		if (option == "suspender") {
			setModalSuspend(true);
		}
		if (option == "agendar") {
			setModalSchedule(true);
		}
	};

	useEffect(() => {
		if (hasViewPermission) {
			setIsLoading(true);
			debounce(() => {
				ProvisionService.getAllBordero(pagina, busca, tipoBordero, statusBordero, typePeriod, dataIniBordero, dataFimBordero, clientFilter).then(
					(result) => {
						setIsLoading(false);
						setRefreshList(false);
						if (result instanceof Error) {
							alert(result.message);
						} else {
							console.log(result);
							setTotalCount(result.totalCount);
							setRows(result.data);
						}
					}
				);
			});
		}
	}, [pesquisar, statusBordero, pagina, refreshList]);

	const handleExtractPDF = (id: string) => {
		setIsLoading(true);
		//setIsExtractPDF(true);
		debounce(() => {
			ProvisionService.getBorderoPDF(id).then((result) => {
				setIsLoading(false);
				//setIsExtractPDF(false);
				if (result instanceof Error) {
					//alert(result.message);
				} else {
					//setModalDetail(true)
				}
			});
		});
	};

	return (
		<>
		<LayoutBaseDePagina
			titulo="Bordero"
			subtitulo="Listagem de Borderos"
			barraDeFerramentas={
				<ToolbarFinancialBordero
					textSearch={busca}
					onChangeTextSearch={(texto) =>
						setSearchParams(
							{ busca: texto, pagina: String(1), tipo: tipoBordero, status: statusBordero, typePeriod, dataini: dataIniBordero, datafim: dataFimBordero, clientFilter },
							{ replace: true }
						)
					}
					searchField={tipoBordero}
					onChangeSearchField={(retorno) =>
						setSearchParams(
							{ busca, pagina: String(1), tipo: retorno, status: statusBordero, typePeriod, dataini: dataIniBordero, datafim: dataFimBordero, clientFilter },
							{ replace: true }
						)
					}
					status={statusBordero}
					onChangeStatus={(retorno) =>
						setSearchParams(
							{ busca, pagina: String(1), tipo: tipoBordero, status: retorno, typePeriod, dataini: dataIniBordero, datafim: dataFimBordero, clientFilter },
							{ replace: true }
						)
					}
					typePeriod={typePeriod}
					onChangeTypePeriod={(retorno) =>
						setSearchParams(
							{ busca, pagina: String(1), tipo: tipoBordero, status: statusBordero, typePeriod: retorno, dataini: dataIniBordero, datafim: dataFimBordero, clientFilter },
							{ replace: true }
						)
					}
					onChangeDateStart={(retorno) =>
						setSearchParams(
							{ busca, pagina: String(1), tipo: tipoBordero, status: statusBordero, typePeriod, dataini: retorno, datafim: dataFimBordero, clientFilter },
							{ replace: true }
						)
					}
					onChangeDateEnd={(retorno) =>
						setSearchParams(
							{ busca, pagina: String(1), tipo: tipoBordero, status: statusBordero, typePeriod, dataini: dataIniBordero, datafim: retorno, clientFilter },
							{ replace: true }
						)
					}
					onChangeClient={(retorno) =>
						setSearchParams(
							{ busca, pagina: String(1), tipo: tipoBordero, status: statusBordero, typePeriod, dataini: dataIniBordero, datafim: dataFimBordero, clientFilter: retorno ?? "" },
							{ replace: true }
						)
					}
					onClickSearch={handleBusca}
				/>
			}
		>
			{modalPayment && (
				<ModalContext
					isCloseModal={() => setModalPayment(false)}
					isOpenModal={modalPayment}
					messageType={"question"}
					showButtonCancel={false}
					textButtonConfirm={"Cancelar"}
					textButtonCancel={"Voltar"}
					onclickConfirm={() => {
						// handleDuaDate();
					}}
					ITitle={"Confirma pagamento de borderô ?"}
					IMessage={[
						<Box style={{ width: "100%" }}>
							<Typography id="modal-modal-description" sx={{ mt: 2, mb: 2, color: "#7d7d7d" }}>
								<VForm
									id="formIdBorderoPayment"
									placeholder="Your placeholder"
									onPointerEnterCapture={() => console.log("Pointer entered")}
									onPointerLeaveCapture={() => console.log("Pointer left")}
									ref={formRef}
									onSubmit={handleBorderoPayment}
								>
									<Grid container direction="column" padding={1} spacing={2}>
										{isLoading && (
											<Grid item>
												<LinearProgress variant="indeterminate" />
											</Grid>
										)}

										<Grid container item direction="row" spacing={1}>
											<Grid item xs={2} sx={{ textAlign: "left" }}>
												Cliente :
											</Grid>
											<Grid item xs={8} sx={{ alignItems: "left" }}>
												<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
													{rowSelect?.sinister.proposal.name}
												</Typography>
											</Grid>
										</Grid>
									</Grid>

									<Grid container item direction="row" gap={3} sx={{ mb: 3, mt: "1rem" }}>
										<Grid item xs={3} sx={{ alignItems: "left" }}>
											<TextField
												fullWidth
												label="Pagamento"
												name="paymentAt"
												disabled={isLoading}
												size="small"
												type="date"
												InputLabelProps={{ shrink: true, required: true }}
												defaultValue={paymentBordero.substring(0, 10)}
												onChange={(e) => {
													setPaymentBordero(e.target.value + " 12:00:00");
												}}
											></TextField>
										</Grid>
										<Grid item xs={3} sx={{ alignItems: "left" }}>
											<TextField
												fullWidth
												label="Valor"
												name="Valor"
												disabled={true}
												value={parseFloat(rowSelect?.value || "0.00").toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
												size="small"
											></TextField>
										</Grid>
										<Grid item xs={3} sx={{ alignItems: "left" }}>
											<TextField
												fullWidth
												label="Valor"
												name="paymentValue"
												disabled={true}
												value={parseFloat(rowSelect?.value || "0.00").toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
												size="small"
											></TextField>
											{/* <VMaskTextField
                        fullWidth
                        label="Valor Pago"
                        name="paymentValue"
                        mask="real"
                        onChange={(e) => {
                          setPaymentValue(e.target.value);
                        }}
                        value={parseFloat(rowSelect?.value || '0.00').toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start"></InputAdornment>
                          ),
                        }}
                      /> */}
										</Grid>
									</Grid>

									<Grid
										container
										sm={12}
										sx={{ border: "none", display: "flex", justifyContent: "center" }}
										display={"flex"}
										flexDirection={smDown ? "column" : "row"}
										padding={2}
										gap={2}
									>
										<Button
											color="primary"
											disableElevation
											variant={"contained"}
											startIcon={<Icon> checkIcon</Icon>}
											onClick={() => {
												formRef.current?.submitForm();
												handleBorderoPayment({
													paymentAt: paymentBordero,
													paymentValue: paymentValue,
												});
												setModalPayment(false);
											}}
										>
											{"Confirmar"}
										</Button>
										<Button
											color="primary"
											disableElevation
											variant={"outlined"}
											startIcon={<Icon> arrow_back_Ico</Icon>}
											onClick={() => {
												setModalPayment(false);
											}}
										>
											{"Voltar"}
										</Button>
									</Grid>
								</VForm>
							</Typography>
						</Box>,
					]}
				/>
			)}

			{(modalProvision || modalReProvision) && (
				<ModalContext
					isCloseModal={() => {
						setModalProvision(false), setModalReProvision(false);
					}}
					isOpenModal={modalProvision || modalReProvision}
					messageType={"question"}
					showButtonCancel={false}
					textButtonConfirm={"Cancelar"}
					textButtonCancel={"Voltar"}
					onclickConfirm={() => {
						// handleDuaDate();
					}}
					ITitle={modalReProvision ? "Confirma reprovisionamento do borderô ?" : "Confirma provisionamento do borderô ?"}
					IMessage={[
						<Box style={{ width: "100%" }}>
							<Typography id="modal-modal-description" sx={{ mt: 2, mb: 2, color: "#7d7d7d" }}>
								<VForm
									id="formIdBorderProvision"
									placeholder="Your placeholder"
									onPointerEnterCapture={() => console.log("Pointer entered")}
									onPointerLeaveCapture={() => console.log("Pointer left")}
									ref={formRef}
									onSubmit={handleBorderoProvision}
								>
									<Grid container direction="column" padding={1} spacing={2}>
										{isLoading && (
											<Grid item>
												<LinearProgress variant="indeterminate" />
											</Grid>
										)}

										<Grid container item direction="row" spacing={1}>
											<Grid item xs={1} sx={{ textAlign: "left" }}>
												Cliente :
											</Grid>
											<Grid item xs={8} sx={{ alignItems: "left" }}>
												<Typography variant="inherit" sx={{ fontWeight: "bold" }}>
													{rowSelect?.sinister.proposal.name}
												</Typography>
											</Grid>
										</Grid>
									</Grid>

									<Grid container item direction="row" gap={3} sx={{ mb: 3, mt: "1rem" }}>
										<Grid item xs={4} sx={{ alignItems: "left" }}>
											<TextField
												fullWidth
												label="Provisionamento"
												name="provisionedAt"
												disabled={isLoading}
												size="small"
												type="date"
												InputLabelProps={{ shrink: true, required: true }}
												defaultValue={new Date().toISOString().substring(0, 10)}
												onChange={(e) => {
													setProvisionBordero(e.target.value + " 12:00:00");
												}}
											></TextField>
										</Grid>
										<Grid item xs={4} sx={{ alignItems: "left" }}>
											<TextField
												fullWidth
												label="Valor"
												name="Valor"
												disabled={true}
												value={parseFloat(rowSelect?.value || "0.00").toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
												size="small"
											></TextField>
										</Grid>
										{/* <Grid item xs={4} sx={{ alignItems: "left" }}>
                      <TextField
                        fullWidth
                        label='Valor Pago'
                        name="paymentValue"
                        disabled={false}
                        value='0.00'
                        size='small'
                      >
                      </TextField>
                    </Grid> */}
									</Grid>

									<Grid
										container
										sm={12}
										sx={{ border: "none", display: "flex", justifyContent: "center" }}
										display={"flex"}
										flexDirection={smDown ? "column" : "row"}
										padding={2}
										gap={2}
									>
										<Button
											color="info"
											disableElevation
											variant={"contained"}
											startIcon={<Icon> checkIcon</Icon>}
											onClick={() => {
												formRef.current?.submitForm();
												handleBorderoProvision({
													provisionedAt: provisionBordero,
												});
												setModalProvision(false);
												// navigate('/boletos/');
											}}
										>
											{"Confirmar"}
										</Button>
										<Button
											color="info"
											disableElevation
											variant={"outlined"}
											startIcon={<Icon> arrow_back_Ico</Icon>}
											onClick={() => {
												setModalProvision(false);
												setModalReProvision(false);
											}}
										>
											{"Voltar"}
										</Button>
									</Grid>
								</VForm>
							</Typography>
						</Box>,
					]}
				/>
			)}

			{modalCancel && (
				<ModalContext
					isCloseModal={() => setModalCancel(false)}
					isOpenModal={modalCancel}
					messageType={"error"}
					enableBackdropClose={false}
					overrideWidthValue="40%"
					showButtonCancel={false}
					overrideHeightValue="auto"
					ITitle="Cancelar item"
					IMessage={[
						<Box style={{ width: "90%" }}>
							<Typography id="modal-modal-description" sx={{ mt: 2, mb: 2, color: "#7d7d7d" }}>
								<VForm
									id="formIdCancel"
									placeholder="Your placeholder"
									onPointerEnterCapture={() => console.log("Pointer entered")}
									onPointerLeaveCapture={() => console.log("Pointer left")}
									ref={formRef}
									onSubmit={handleCancel}
								>
									<Grid container direction="column" padding={1} spacing={2}>
										{isLoading && (
											<Grid item>
												<LinearProgress variant="indeterminate" />
											</Grid>
										)}
										<Grid container item direction="row" spacing={2}>
											<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
												<Grid container item direction="row" spacing={1}>
													<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
														<Typography variant="caption" color={"text.secondary"}>
															Status
														</Typography>
														<Typography variant="h5" sx={{ color: "red" }}>
															Cancelado
														</Typography>
													</Grid>

													<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
														<Typography variant="caption" color={"text.secondary"}>
															Data
														</Typography>
														<Typography variant="subtitle2" sx={{ color: "gray" }}></Typography>
													</Grid>
												</Grid>
											</Grid>
										</Grid>

										<Grid container item direction="row" spacing={2}>
											<Grid item xs={12}>
												<VTextField
													fullWidth
													label="Descreva o motivo do cancelamento"
													name="canceledReason"
													disabled={isLoading}
													multiline
													rows={4}
												></VTextField>
											</Grid>
										</Grid>
									</Grid>
									<Grid container item direction="row"></Grid>

									<Grid
										container
										sm={12}
										sx={{
											border: "none",
											display: "flex",
											justifyContent: "center",
										}}
										display={"flex"}
										flexDirection={smDown ? "column" : "row"}
										padding={2}
										gap={2}
									>
										<Button
											color="primary"
											disableElevation
											variant={"outlined"}
											startIcon={<Icon> arrow_back_Ico</Icon>}
											onClick={() => {
												//formRef.current?.submitForm();
												setModalCancel(false);
											}}
										>
											{"Voltar"}
										</Button>
										<Button
											color="primary"
											disableElevation
											variant={"contained"}
											startIcon={<Icon> checkIcon</Icon>}
											onClick={() => {
												formRef.current?.submitForm();
												setModalCancel(false);
											}}
										>
											{"Confirmar"}
										</Button>
									</Grid>
								</VForm>
							</Typography>
						</Box>,
					]}
				/>
			)}

			{hasViewPermission && (
				<TableContainer
					component={Paper}
					variant="elevation"
					sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
				>
					<Table size="small" stickyHeader aria-label="Tabela de borderôs">
						<TableHead>
							<TableRow sx={{ backgroundColor: "grey.100" }}>
								<TableCell sx={{ width: "1%" }}>
									<Typography variant="button">#</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Nome</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Parceiro</Typography>
								</TableCell>
								<TableCell align="right">
									<Typography variant="button">Valor</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Abertura</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Provisão</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Limite</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="button">Status</Typography>
								</TableCell>
								<TableCell align="right" sx={{ p: 0, width: "1%" }}>
									<Typography variant="button">Ações</Typography>
								</TableCell>
							</TableRow>
							{isLoading && (
								<TableRow>
									<TableCell colSpan={9} sx={{ p: 0 }}>
										<LinearProgress variant="indeterminate" />
									</TableCell>
								</TableRow>
							)}
						</TableHead>
						<TableBody>
							{rows.length === 0 && !isLoading && (
								<TableRow>
									<TableCell colSpan={9} align="center" sx={{ py: 6 }}>
										<Typography variant="body2" color="text.secondary">
											{Environment.LISTAGEM_VAZIA}
										</Typography>
									</TableCell>
								</TableRow>
							)}
							{rows.map((row) => {
								const statusColors = getBorderoStatusChipColors(row.status);
								return (
									<TableRow key={row.id} hover>
										<TableCell>
											<Typography variant="body2" color="text.secondary" fontWeight={600}>
												{row.sequencial}
											</Typography>
										</TableCell>
										<TableCell>
											<HtmlTooltip
												title={
													<Box>
														<Typography variant="subtitle2" fontWeight="bold">
															{row.sinister.proposal.name}
														</Typography>
													</Box>
												}
												arrow
												placement="top"
											>
												<Box>
													<Typography
														variant="body2"
														sx={{ maxWidth: "22ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
													>
														{row.sinister.proposal.name}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{row?.sinister.proposal.document.length > 11
															? `${row?.sinister.proposal.document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "$1.$2.$3/$4-$5")}`
															: `${row?.sinister.proposal.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4")}`}
													</Typography>
												</Box>
											</HtmlTooltip>
										</TableCell>

										<TableCell>
											<HtmlTooltip
												title={
													<Box>
														<Typography variant="subtitle2" fontWeight="bold">
															{row.client.name}
														</Typography>
													</Box>
												}
												arrow
												placement="top"
											>
												<Box>
													<Typography
														variant="body2"
														sx={{ maxWidth: "22ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
													>
														{row.client.name}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{row?.client.document.length > 11
															? `${row?.client.document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, "$1.$2.$3/$4-$5")}`
															: `${row?.client.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, "$1.$2.$3-$4")}`}
													</Typography>
												</Box>
											</HtmlTooltip>
										</TableCell>

										<TableCell align="right">
											<Typography variant="body2" fontWeight={500}>
												{parseFloat(`${row.value}`).toLocaleString("pt-br", { style: "currency", currency: "BRL" })}
											</Typography>
										</TableCell>

										<TableCell align="center">
											<Stack spacing={0.2} alignItems="center">
												<Typography variant="body2" color="text.secondary">
													{moment(row.createdAt).format("L")}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{moment(row.createdAt).format("HH:mm:ss")}
												</Typography>
											</Stack>
										</TableCell>

										<TableCell align="center">
											<Typography variant="body2" color="text.secondary">
												{row.provisionedAt != null ? moment(row.provisionedAt).format("L") : "-"}
											</Typography>
										</TableCell>

										<TableCell align="center">
											<Typography variant="body2" color="text.secondary">
												{row.dueDate ? moment(row.dueDate).format("L") : "-"}
											</Typography>
										</TableCell>

										<TableCell>
											<Stack spacing={0.3}>
												<Chip
													size="medium"
													label={getBorderoStatusLabel(row.status)}
													sx={{
														color: statusColors.color,
														backgroundColor: statusColors.bg,
														fontWeight: 500,
														border: "0.5px solid #f1f1f1",
														width: "fit-content",
													}}
												/>
												{row.status === "paid" && row.paymentAt && (
													<Typography variant="caption" sx={{ color: statusColors.color }}>
														em {moment(row.paymentAt).format("L")}
													</Typography>
												)}
												{row.status === "canceled" && row.canceledAt && (
													<Typography variant="caption" sx={{ color: statusColors.color }}>
														em {moment(row.canceledAt).format("L")}
													</Typography>
												)}
												{row.status === "scheduled_financeiro" && row.scheduledDate && (
													<Typography variant="caption" sx={{ color: statusColors.color }}>
														p/ {moment(row.scheduledDate).format("L")}
													</Typography>
												)}
											</Stack>
										</TableCell>
										<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
											<Tooltip title="Pdf Extrato do Sinistro" placement="top">
												<IconButton size="small" disabled={isLoading} onClick={() => handleExtractPDF(row?.id ? row.id : "0")}>
													<Icon sx={{ color: "#7924c7", fontWeight: "normal" }}>picture_as_pdf_outlined_Icon</Icon>
												</IconButton>
											</Tooltip>
											<RowMenuBordero goTo={goTo} row={row} />
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>

					{totalCount > 0 && (
						<Box display="flex" flexDirection={smDown ? "column" : "row"} justifyContent="space-between" alignItems="center" px={2} py={1.5}>
							<Typography variant="body2" color="text.secondary">
								Exibindo {pagina * Environment.LIMITE_LINHAS - (Environment.LIMITE_LINHAS - 1)}
								{" - "}
								{Environment.LIMITE_LINHAS * (pagina - 1) + Environment.LIMITE_LINHAS * pagina < totalCount
									? Environment.LIMITE_LINHAS * pagina
									: totalCount}{" "}
								de <strong>{totalCount}</strong> registro(s)
							</Typography>
							<Pagination
								page={pagina}
								count={Math.ceil(totalCount / Environment.LIMITE_LINHAS)}
								onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
							/>
						</Box>
					)}
				</TableContainer>
			)}
		</LayoutBaseDePagina>

			<ModalSuspendBorderoFinanceiro
				open={modalSuspend}
				borderoId={idBordero}
				onClose={() => setModalSuspend(false)}
				onSuccess={() => setRefreshList(true)}
			/>

			<ModalScheduleBordero
				open={modalSchedule}
				borderoId={idBordero}
				onClose={() => setModalSchedule(false)}
				onSuccess={() => setRefreshList(true)}
			/>
		</>
	);
};
